<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import format from 'date-fns/format';

import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';

const { t } = useI18n();

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null,
  },
});

// Dashboard state
const dashboardStats = ref(null);
const isDashboardLoading = ref(false);

// Monitor state
const monitorProfiles = ref([]);
const monitorAlerts = ref([]);
const isMonitorLoading = ref(false);
const newProfileUsername = ref('');
const newProfileKeywords = ref('');
const newProfileType = ref('candidate');
const newCandidateName = ref('');
const newProfileNotes = ref('');
const newProfilePriority = ref(0);
const newFacebookPageId = ref('');
const newMonitorFacebook = ref(false);
const newPhoneNumber = ref('');
const isAddingProfile = ref(false);
const alertFilter = ref('all');
const alertProfileFilter = ref('');
const profileTypeFilter = ref('');
const showAddForm = ref(false);

// Edit state
const editingProfileId = ref(null);
const editProfileUsername = ref('');
const editProfileKeywords = ref('');
const editProfileType = ref('candidate');
const editCandidateName = ref('');
const editProfileNotes = ref('');
const editProfilePriority = ref(0);
const editFacebookPageId = ref('');
const editMonitorFacebook = ref(false);
const editPhoneNumber = ref('');
const isSavingProfile = ref(false);

// AI toggle state
const aiEnabled = ref(true);
const isTogglingAi = ref(false);

// Pagination state
const alertsPage = ref(1);
const alertsPerPage = 10;

// Computed
const filteredProfiles = computed(() => {
  if (!profileTypeFilter.value) return monitorProfiles.value;
  return monitorProfiles.value.filter(
    p => p.profile_type === profileTypeFilter.value
  );
});

const filteredAlerts = computed(() => {
  let alerts = monitorAlerts.value;

  // Filter by profile
  if (alertProfileFilter.value) {
    alerts = alerts.filter(
      a => a.profile_id === alertProfileFilter.value || a.instagram_monitored_profile_id === alertProfileFilter.value
    );
  }

  // Filter by type
  if (alertFilter.value === 'unread')
    return alerts.filter(a => !a.read);
  if (alertFilter.value === 'positive')
    return alerts.filter(a => a.sentiment === 'positive');
  if (alertFilter.value === 'negative')
    return alerts.filter(a => a.sentiment === 'negative');
  if (alertFilter.value === 'high_risk')
    return alerts.filter(
      a => a.risk_level === 'high' || a.risk_level === 'critical'
    );
  return alerts;
});

const paginatedAlerts = computed(() => {
  const start = (alertsPage.value - 1) * alertsPerPage;
  return filteredAlerts.value.slice(start, start + alertsPerPage);
});

const totalAlertPages = computed(() => {
  return Math.ceil(filteredAlerts.value.length / alertsPerPage) || 1;
});

const urgentAlerts = computed(() => {
  return monitorAlerts.value.filter(
    a =>
      !a.read &&
      (a.risk_level === 'high' || a.risk_level === 'critical') &&
      (a.candidate_relevant === true || a.topic === 'candidate_related')
  );
});

const candidateCount = computed(() => {
  return monitorProfiles.value.filter(
    p => p.profile_type === 'candidate'
  ).length;
});

const leadershipCount = computed(() => {
  return monitorProfiles.value.filter(
    p => p.profile_type === 'leadership'
  ).length;
});

const adversaryCount = computed(() => {
  return monitorProfiles.value.filter(
    p => p.profile_type === 'adversary'
  ).length;
});

const unreadAlertCount = computed(() => {
  return monitorAlerts.value.filter(a => !a.read).length;
});

const todayAlertCount = computed(() => {
  const today = new Date().toISOString().slice(0, 10);
  return monitorAlerts.value.filter(
    a => a.created_at && a.created_at.startsWith(today)
  ).length;
});

// Helper functions
function sentimentClass(sentiment) {
  if (sentiment === 'positive') return 'bg-n-teal-9';
  if (sentiment === 'negative') return 'bg-n-ruby-9';
  return 'bg-n-slate-9';
}

function riskClass(risk) {
  if (risk === 'low') return 'bg-n-slate-9';
  if (risk === 'medium') return 'bg-n-amber-9';
  if (risk === 'high') return 'bg-n-orange-9';
  if (risk === 'critical') return 'bg-n-ruby-9';
  return 'bg-n-slate-9';
}

function profileTypeClass(profileType) {
  if (profileType === 'candidate') return 'bg-n-teal-9';
  if (profileType === 'leadership') return 'bg-n-blue-9';
  if (profileType === 'adversary') return 'bg-n-ruby-9';
  if (profileType === 'listening') return 'bg-n-violet-9';
  return 'bg-n-slate-9';
}

function profileTypeLabel(profileType) {
  const map = {
    candidate: t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_CANDIDATE'),
    leadership: t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_LEADERSHIP'),
    allied: t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_ALLIED'),
    adversary: t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_ADVERSARY'),
    listening: t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_LISTENING'),
  };
  return map[profileType] || profileType;
}

function sentimentLabel(sentiment) {
  const map = {
    positive: t('INSTAGRAM_MEDIA_REPORTS.MONITOR_FILTER_POSITIVE'),
    negative: t('INSTAGRAM_MEDIA_REPORTS.MONITOR_FILTER_NEGATIVE'),
    neutral: 'Neutro',
  };
  return map[sentiment] || sentiment;
}

function riskLabel(risk) {
  const map = {
    low: 'Baixo',
    medium: 'Médio',
    high: 'Alto',
    critical: 'Crítico',
  };
  return map[risk] || risk;
}

function platformLabel(platform) {
  const map = {
    instagram: 'Instagram',
    facebook: 'Facebook',
    twitter: 'X/Twitter',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    web: 'Web',
  };
  return map[platform] || (platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : '');
}

function alertTypeLabel(alertType) {
  const map = {
    new_post: 'Nova publicação',
    keyword: 'Palavra-chave',
    mention: 'Menção',
    news: 'Notícia',
    social_post: 'Post social',
    other: 'Outro',
  };
  return map[alertType] || alertType;
}

function platformClass(platform) {
  if (platform === 'instagram') return 'bg-n-pink-9';
  if (platform === 'facebook') return 'bg-n-blue-9';
  if (platform === 'twitter') return 'bg-n-slate-12';
  if (platform === 'tiktok') return 'bg-n-slate-12';
  if (platform === 'youtube') return 'bg-n-ruby-9';
  if (platform === 'web') return 'bg-n-slate-9';
  return 'bg-n-slate-9';
}

function formatDate(dateStr) {
  try {
    return format(new Date(dateStr), 'dd/MM');
  } catch {
    return dateStr;
  }
}

// Date helpers
function currentMonthStart() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
}

// API functions
async function fetchDashboardStats() {
  if (!props.selectedInbox) return;
  isDashboardLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.getDashboardStats({
      inboxId: props.selectedInbox.id,
      sinceDate: currentMonthStart(),
    });
    dashboardStats.value = response.data;
  } catch {
    // silently fail
  } finally {
    isDashboardLoading.value = false;
  }
}

async function fetchMonitorData() {
  if (!props.selectedInbox) return;
  isMonitorLoading.value = true;
  try {
    const [profilesRes, alertsRes] = await Promise.all([
      InstagramMediaReportsAPI.getMonitoredProfiles({
        inboxId: props.selectedInbox.id,
        profileType: profileTypeFilter.value || undefined,
      }),
      InstagramMediaReportsAPI.getAllAlerts({
        inboxId: props.selectedInbox.id,
        sinceDate: currentMonthStart(),
      }),
    ]);
    monitorProfiles.value = profilesRes.data;
    monitorAlerts.value = alertsRes.data;
  } catch {
    // silently fail
  } finally {
    isMonitorLoading.value = false;
  }
}

async function addMonitorProfile() {
  if (!props.selectedInbox || !newProfileUsername.value.trim()) return;
  isAddingProfile.value = true;
  try {
    const keywords = newProfileKeywords.value
      .split(',')
      .map(k => k.trim())
      .filter(Boolean);
    await InstagramMediaReportsAPI.createMonitoredProfile({
      inboxId: props.selectedInbox.id,
      username: newProfileUsername.value.trim().replace(/^@/, ''),
      keywords,
      profileType: newProfileType.value,
      candidateName: newCandidateName.value.trim(),
      notes: newProfileNotes.value.trim(),
      priority: newProfilePriority.value,
      facebookPageId: newFacebookPageId.value.trim(),
      monitorFacebook: newMonitorFacebook.value,
      phoneNumber: newPhoneNumber.value.trim(),
    });
    newProfileUsername.value = '';
    newProfileKeywords.value = '';
    newProfileType.value = 'candidate';
    newCandidateName.value = '';
    newProfileNotes.value = '';
    newProfilePriority.value = 0;
    newFacebookPageId.value = '';
    newMonitorFacebook.value = false;
    newPhoneNumber.value = '';
    showAddForm.value = false;
    fetchMonitorData();
  } catch {
    // silently fail
  } finally {
    isAddingProfile.value = false;
  }
}

async function removeProfile(profileId) {
  if (!props.selectedInbox) return;
  const profile = monitorProfiles.value.find(p => p.id === profileId);
  const label = profile ? `@${profile.username}` : `#${profileId}`;
  if (!window.confirm(`Deseja excluir o perfil ${label}?`)) return;
  try {
    await InstagramMediaReportsAPI.deleteMonitoredProfile({
      inboxId: props.selectedInbox.id,
      profileId,
    });
    useAlert(`Perfil ${label} removido com sucesso`);
    fetchMonitorData();
  } catch {
    useAlert('Erro ao remover perfil. Tente novamente.');
  }
}

async function toggleProfile(profile) {
  if (!props.selectedInbox) return;
  try {
    await InstagramMediaReportsAPI.updateMonitoredProfile({
      inboxId: props.selectedInbox.id,
      profileId: profile.id,
      active: !profile.active,
    });
    fetchMonitorData();
  } catch {
    // silently fail
  }
}

async function checkNow(profileId) {
  if (!props.selectedInbox) return;
  try {
    await InstagramMediaReportsAPI.checkProfileNow({
      inboxId: props.selectedInbox.id,
      profileId,
    });
    setTimeout(fetchMonitorData, 5000);
  } catch {
    // silently fail
  }
}

async function markRead(alertId) {
  if (!props.selectedInbox) return;
  try {
    await InstagramMediaReportsAPI.markAlertRead({
      inboxId: props.selectedInbox.id,
      alertId,
    });
    const alert = monitorAlerts.value.find(a => a.id === alertId);
    if (alert) alert.read = true;
  } catch {
    // silently fail
  }
}

function startEditing(profile) {
  editingProfileId.value = profile.id;
  editProfileUsername.value = profile.username;
  editProfileKeywords.value = (profile.keywords || []).join(', ');
  editProfileType.value = profile.profile_type || 'leadership';
  editCandidateName.value = profile.candidate_name || '';
  editProfileNotes.value = profile.notes || '';
  editProfilePriority.value = profile.priority || 0;
  editFacebookPageId.value = profile.facebook_page_id || '';
  editMonitorFacebook.value = profile.monitor_facebook || false;
  editPhoneNumber.value = profile.phone_number || '';
}

function cancelEditing() {
  editingProfileId.value = null;
}

async function saveProfile() {
  if (!props.selectedInbox || !editingProfileId.value) return;
  isSavingProfile.value = true;
  try {
    const keywords = editProfileKeywords.value
      .split(',')
      .map(k => k.trim())
      .filter(Boolean);
    await InstagramMediaReportsAPI.updateMonitoredProfile({
      inboxId: props.selectedInbox.id,
      profileId: editingProfileId.value,
      username: editProfileUsername.value.trim().replace(/^@/, ''),
      keywords,
      profileType: editProfileType.value,
      candidateName: editCandidateName.value.trim(),
      notes: editProfileNotes.value.trim(),
      priority: editProfilePriority.value,
      facebookPageId: editFacebookPageId.value.trim(),
      monitorFacebook: editMonitorFacebook.value,
      phoneNumber: editPhoneNumber.value.trim(),
    });
    editingProfileId.value = null;
    fetchMonitorData();
  } catch {
    // silently fail
  } finally {
    isSavingProfile.value = false;
  }
}

async function fetchAiStatus() {
  if (!props.selectedInbox) return;
  try {
    const response = await InstagramMediaReportsAPI.getListeningConfig({
      inboxId: props.selectedInbox.id,
    });
    if (response.data && response.data.configured) {
      aiEnabled.value = response.data.ai_enabled !== false;
    }
  } catch {
    // silently fail
  }
}

async function toggleAi() {
  if (!props.selectedInbox) return;
  isTogglingAi.value = true;
  try {
    await InstagramMediaReportsAPI.saveListeningConfig({
      inboxId: props.selectedInbox.id,
      candidateName: undefined,
      candidateUsername: undefined,
      hashtags: undefined,
      watchTerms: undefined,
      adversaryNames: undefined,
      active: undefined,
      aiEnabled: !aiEnabled.value,
    });
    aiEnabled.value = !aiEnabled.value;
  } catch {
    // silently fail
  } finally {
    isTogglingAi.value = false;
  }
}

function loadData() {
  if (props.selectedInbox) {
    fetchDashboardStats();
    fetchMonitorData();
    fetchAiStatus();
  }
}

watch(() => props.selectedInbox, loadData);
watch(alertFilter, () => { alertsPage.value = 1; });
watch(alertProfileFilter, () => { alertsPage.value = 1; });

onMounted(loadData);
</script>

<template>
  <div>
    <div
      v-if="!selectedInbox"
      class="flex items-center justify-center py-12 text-n-slate-11"
    >
      {{ $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_SELECT_INBOX') }}
    </div>

    <template v-else>
      <div
        v-if="isMonitorLoading || isDashboardLoading"
        class="flex items-center justify-center py-12"
      >
        <Spinner />
      </div>

      <template v-else>
        <div class="flex items-center justify-between bg-n-solid-2 rounded-lg p-3 border border-n-weak mb-6">
          <div class="flex items-center gap-3">
            <span class="text-sm font-medium text-n-slate-12">Pesquisa por IA</span>
            <span class="text-xs text-n-slate-11">
              {{ aiEnabled ? 'Ativada — busca web, análise de sentimento e risco' : 'Pausada — apenas monitoramento via API do Instagram/Facebook' }}
            </span>
          </div>
          <button
            class="px-4 py-1.5 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50"
            :class="aiEnabled ? 'bg-n-teal-9 text-white border-n-teal-9 hover:bg-n-teal-10' : 'bg-n-slate-3 text-n-slate-11 border-n-weak hover:border-n-slate-7'"
            :disabled="isTogglingAi"
            @click="toggleAi"
          >
            <Spinner v-if="isTogglingAi" size="small" />
            <span v-else>{{ aiEnabled ? 'Pausar IA' : 'Ativar IA' }}</span>
          </button>
        </div>

        <div v-if="dashboardStats" class="mb-6">
          <div class="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.DASHBOARD_TOTAL_PROFILES') }}
              </p>
              <p class="text-xl font-semibold text-n-slate-12">
                {{ dashboardStats.total_profiles || 0 }}
              </p>
            </div>
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_CANDIDATE') }}
              </p>
              <p class="text-xl font-semibold text-n-teal-11">
                {{ dashboardStats.candidates || candidateCount }}
              </p>
            </div>
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.DASHBOARD_LEADERSHIPS') }}
              </p>
              <p class="text-xl font-semibold text-n-blue-11">
                {{ dashboardStats.leaderships || leadershipCount }}
              </p>
            </div>
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.DASHBOARD_ADVERSARIES') }}
              </p>
              <p class="text-xl font-semibold text-n-ruby-11">
                {{ dashboardStats.adversaries || adversaryCount }}
              </p>
            </div>
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.DASHBOARD_TOTAL_ALERTS') }}
              </p>
              <p class="text-xl font-semibold text-n-slate-12">
                {{ dashboardStats.total_alerts || monitorAlerts.length }}
              </p>
            </div>
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.DASHBOARD_UNREAD') }}
              </p>
              <p class="text-xl font-semibold text-n-amber-11">
                {{ dashboardStats.unread || unreadAlertCount }}
              </p>
            </div>
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.DASHBOARD_TODAY') }}
              </p>
              <p class="text-xl font-semibold text-n-violet-11">
                {{ dashboardStats.today || todayAlertCount }}
              </p>
            </div>
          </div>

          <div class="grid grid-cols-3 gap-3 mb-4">
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">{{ $t('INSTAGRAM_MEDIA_REPORTS.SENTIMENT_POSITIVE') }}</p>
              <p class="text-xl font-semibold text-n-teal-11">{{ dashboardStats.sentiment_positive || 0 }}</p>
            </div>
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">{{ $t('INSTAGRAM_MEDIA_REPORTS.SENTIMENT_NEGATIVE') }}</p>
              <p class="text-xl font-semibold text-n-ruby-11">{{ dashboardStats.sentiment_negative || 0 }}</p>
            </div>
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">{{ $t('INSTAGRAM_MEDIA_REPORTS.SENTIMENT_NEUTRAL') }}</p>
              <p class="text-xl font-semibold text-n-slate-11">{{ dashboardStats.sentiment_neutral || 0 }}</p>
            </div>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">{{ $t('INSTAGRAM_MEDIA_REPORTS.RISK_LOW') }}</p>
              <p class="text-xl font-semibold text-n-slate-11">{{ dashboardStats.risk_low || 0 }}</p>
            </div>
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">{{ $t('INSTAGRAM_MEDIA_REPORTS.RISK_MEDIUM') }}</p>
              <p class="text-xl font-semibold text-n-amber-11">{{ dashboardStats.risk_medium || 0 }}</p>
            </div>
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">{{ $t('INSTAGRAM_MEDIA_REPORTS.RISK_HIGH') }}</p>
              <p class="text-xl font-semibold text-n-orange-11">{{ dashboardStats.risk_high || 0 }}</p>
            </div>
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">{{ $t('INSTAGRAM_MEDIA_REPORTS.RISK_CRITICAL') }}</p>
              <p class="text-xl font-semibold text-n-ruby-11">{{ dashboardStats.risk_critical || 0 }}</p>
            </div>
          </div>

          <div class="grid grid-cols-3 gap-3 mb-6">
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">{{ $t('INSTAGRAM_MEDIA_REPORTS.PLATFORM_INSTAGRAM') }}</p>
              <p class="text-xl font-semibold text-n-pink-11">{{ dashboardStats.platform_instagram || 0 }}</p>
            </div>
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">{{ $t('INSTAGRAM_MEDIA_REPORTS.PLATFORM_FACEBOOK') }}</p>
              <p class="text-xl font-semibold text-n-blue-11">{{ dashboardStats.platform_facebook || 0 }}</p>
            </div>
            <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
              <p class="text-xs text-n-slate-11 mb-1">{{ $t('INSTAGRAM_MEDIA_REPORTS.PLATFORM_WEB') }}</p>
              <p class="text-xl font-semibold text-n-slate-11">{{ dashboardStats.platform_web || 0 }}</p>
            </div>
          </div>
        </div>

        <div v-if="urgentAlerts.length" class="bg-n-solid-2 rounded-lg p-4 border-2 border-n-ruby-7 mb-6">
          <h3 class="text-lg font-semibold mb-4 text-n-ruby-11">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_URGENT_ALERTS') }}</h3>
          <div class="space-y-3">
            <div v-for="alert in urgentAlerts" :key="'urgent-' + alert.id" class="flex items-start gap-4 p-3 rounded-lg border-2 border-n-ruby-7 bg-n-ruby-2">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1 flex-wrap">
                  <span class="text-sm font-medium text-n-slate-12">@{{ alert.profile_username }}</span>
                  <span class="text-xs px-2 py-0.5 rounded-full bg-n-amber-3 text-n-amber-11">{{ alert.alert_type }}</span>
                  <span class="text-xs px-2 py-0.5 rounded-full text-white" :class="sentimentClass(alert.sentiment)">{{ alert.sentiment || 'N/A' }}</span>
                  <span class="text-xs px-2 py-0.5 rounded-full text-white" :class="riskClass(alert.risk_level)">{{ alert.risk_level || 'N/A' }}</span>
                </div>
                <p v-if="alert.caption" class="text-sm text-n-slate-11 truncate">{{ alert.caption }}</p>
                <p v-if="alert.ai_summary" class="text-xs text-n-slate-11 mt-1">{{ alert.ai_summary }}</p>
                <div class="flex items-center gap-3 mt-1 text-xs text-n-slate-9">
                  <span v-if="alert.media_timestamp">Publicado: {{ formatDate(alert.media_timestamp) }}</span>
                  <span v-if="alert.source_platform" class="px-1.5 py-0.5 rounded bg-n-solid-3 text-n-slate-11">{{ alert.source_platform }}</span>
                  <span>Detectado: {{ formatDate(alert.created_at) }}</span>
                </div>
              </div>
              <div class="flex gap-2 flex-shrink-0">
                <a v-if="alert.permalink" :href="alert.permalink" target="_blank" rel="noopener noreferrer" class="px-3 py-1 text-xs rounded-lg border border-n-weak text-n-blue-11 hover:border-n-blue-7 transition-colors">{{ $t('INSTAGRAM_MEDIA_REPORTS.OPEN_LINK') }}</a>
              </div>
            </div>
          </div>
        </div>

        <div v-if="monitorProfiles.length" class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_ACTIVITY_RANKING') }}</h3>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-n-weak">
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-11">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_USERNAME') }}</th>
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-11">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE') }}</th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_ALERTS_7D') }}</th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_LAST_CHECKED') }}</th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_STATUS') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="profile in monitorProfiles" :key="'rank-' + profile.id" class="border-b border-n-weak">
                  <td class="py-2 px-3 text-n-slate-12 font-medium">@{{ profile.username }}</td>
                  <td class="py-2 px-3">
                    <span class="text-xs px-2 py-0.5 rounded-full text-white" :class="profileTypeClass(profile.profile_type)">{{ profileTypeLabel(profile.profile_type) }}</span>
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-12">{{ profile.alerts_7d || 0 }}</td>
                  <td class="py-2 px-3 text-center text-n-slate-11 text-xs">{{ profile.last_checked_at ? formatDate(profile.last_checked_at) : '-' }}</td>
                  <td class="py-2 px-3 text-center">
                    <span class="text-xs px-2 py-0.5 rounded-full" :class="profile.active ? 'bg-n-teal-3 text-n-teal-11' : 'bg-n-slate-3 text-n-slate-11'">
                      {{ profile.active ? $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_ACTIVE') : $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_PAUSED') }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
          <button class="flex items-center justify-between w-full text-lg font-semibold text-n-slate-12" @click="showAddForm = !showAddForm">
            <span>{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_ADD_TITLE') }}</span>
            <span class="text-sm text-n-slate-11">{{ showAddForm ? '-' : '+' }}</span>
          </button>
          <div v-if="showAddForm" class="mt-4 space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_USERNAME') }}</label>
                <input v-model="newProfileUsername" type="text" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12" placeholder="@username" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_PROFILE_TYPE') }}</label>
                <select v-model="newProfileType" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12">
                  <option value="candidate">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_CANDIDATE') }}</option>
                  <option value="leadership">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_LEADERSHIP') }}</option>
                  <option value="allied">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_ALLIED') }}</option>
                  <option value="adversary">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_ADVERSARY') }}</option>
                </select>
              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_KEYWORDS') }}</label>
                <input v-model="newProfileKeywords" type="text" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12" :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.MONITOR_KEYWORDS_PLACEHOLDER')" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_CANDIDATE_NAME') }}</label>
                <input v-model="newCandidateName" type="text" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12" />
              </div>
            </div>
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_NOTES') }}</label>
              <textarea v-model="newProfileNotes" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12 resize-none" rows="2" />
            </div>
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_PRIORITY') }}</label>
                <input v-model.number="newProfilePriority" type="number" min="0" max="10" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_PHONE_NUMBER') }}</label>
                <input v-model="newPhoneNumber" type="text" placeholder="5562999999999" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_FACEBOOK_PAGE_ID') }}</label>
                <input v-model="newFacebookPageId" type="text" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12" />
              </div>
              <div class="flex items-end pb-1">
                <label class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer">
                  <input v-model="newMonitorFacebook" type="checkbox" class="rounded border-n-weak" />
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_FACEBOOK_ENABLED') }}
                </label>
              </div>
            </div>
            <div class="flex justify-end">
              <button class="px-4 py-1.5 text-sm font-medium rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-colors disabled:opacity-50" :disabled="isAddingProfile || !newProfileUsername.trim()" @click="addMonitorProfile">
                <Spinner v-if="isAddingProfile" size="small" />
                <span v-else>{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_ADD_BUTTON') }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_PROFILES_TITLE') }}</h3>
          <div class="flex gap-2 mb-4">
            <button class="px-3 py-1 text-xs rounded-lg border transition-colors" :class="profileTypeFilter === '' ? 'bg-n-blue-9 text-white border-n-blue-9' : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'" @click="profileTypeFilter = ''">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_FILTER_ALL') }}</button>
            <button class="px-3 py-1 text-xs rounded-lg border transition-colors" :class="profileTypeFilter === 'candidate' ? 'bg-n-teal-9 text-white border-n-teal-9' : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'" @click="profileTypeFilter = 'candidate'">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_CANDIDATE') }}</button>
            <button class="px-3 py-1 text-xs rounded-lg border transition-colors" :class="profileTypeFilter === 'leadership' ? 'bg-n-blue-9 text-white border-n-blue-9' : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'" @click="profileTypeFilter = 'leadership'">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_LEADERSHIP') }}</button>
            <button class="px-3 py-1 text-xs rounded-lg border transition-colors" :class="profileTypeFilter === 'allied' ? 'bg-n-green-9 text-white border-n-green-9' : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'" @click="profileTypeFilter = 'allied'">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_ALLIED') }}</button>
            <button class="px-3 py-1 text-xs rounded-lg border transition-colors" :class="profileTypeFilter === 'adversary' ? 'bg-n-ruby-9 text-white border-n-ruby-9' : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'" @click="profileTypeFilter = 'adversary'">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_ADVERSARY') }}</button>
          </div>
          <div v-if="filteredProfiles.length" class="space-y-3">
            <div v-for="profile in filteredProfiles" :key="profile.id" class="p-3 rounded-lg border border-n-weak">
              <template v-if="editingProfileId === profile.id">
                <div class="space-y-3">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label class="text-xs text-n-slate-11 mb-1 block">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_USERNAME') }}</label>
                      <input v-model="editProfileUsername" type="text" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12" />
                    </div>
                    <div>
                      <label class="text-xs text-n-slate-11 mb-1 block">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_PROFILE_TYPE') }}</label>
                      <select v-model="editProfileType" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12">
                        <option value="candidate">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_CANDIDATE') }}</option>
                        <option value="leadership">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_LEADERSHIP') }}</option>
                        <option value="allied">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_ALLIED') }}</option>
                        <option value="adversary">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_TYPE_ADVERSARY') }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label class="text-xs text-n-slate-11 mb-1 block">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_KEYWORDS') }}</label>
                      <input v-model="editProfileKeywords" type="text" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12" />
                    </div>
                    <div>
                      <label class="text-xs text-n-slate-11 mb-1 block">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_CANDIDATE_NAME') }}</label>
                      <input v-model="editCandidateName" type="text" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12" />
                    </div>
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label class="text-xs text-n-slate-11 mb-1 block">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_PHONE_NUMBER') }}</label>
                      <input v-model="editPhoneNumber" type="text" placeholder="5562999999999" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12" />
                    </div>
                    <div>
                      <label class="text-xs text-n-slate-11 mb-1 block">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_FACEBOOK_PAGE_ID') }}</label>
                      <input v-model="editFacebookPageId" type="text" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12" />
                    </div>
                    <div class="flex items-end pb-1">
                      <label class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer">
                        <input v-model="editMonitorFacebook" type="checkbox" class="rounded border-n-weak" />
                        {{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_FACEBOOK_ENABLED') }}
                      </label>
                    </div>
                  </div>
                  <div class="flex justify-end gap-2">
                    <button class="px-3 py-1 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-n-slate-7 transition-colors" @click="cancelEditing">Cancelar</button>
                    <button class="px-3 py-1 text-xs rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-colors disabled:opacity-50" :disabled="isSavingProfile" @click="saveProfile">
                      <Spinner v-if="isSavingProfile" size="small" />
                      <span v-else>Salvar</span>
                    </button>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="flex items-center gap-4">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <p class="text-sm font-medium text-n-slate-12">@{{ profile.username }}</p>
                      <span class="text-xs px-2 py-0.5 rounded-full text-white" :class="profileTypeClass(profile.profile_type)">{{ profileTypeLabel(profile.profile_type) }}</span>
                    </div>
                    <p v-if="profile.keywords && profile.keywords.length" class="text-xs text-n-slate-11 mt-1">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_KEYWORDS') }}: {{ profile.keywords.join(', ') }}</p>
                    <p v-if="profile.candidate_name" class="text-xs text-n-slate-11 mt-1">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_CANDIDATE_NAME') }}: {{ profile.candidate_name }}</p>
                    <p v-if="profile.last_error" class="text-xs text-n-ruby-11 mt-1">{{ profile.last_error }}</p>
                  </div>
                  <div class="flex items-center gap-3 text-xs text-n-slate-11 flex-shrink-0">
                    <span v-if="profile.unread_count" class="px-2 py-0.5 rounded-full bg-n-ruby-9 text-white font-medium">{{ profile.unread_count }}</span>
                    <span v-if="profile.negative_count" class="px-2 py-0.5 rounded-full bg-n-amber-9 text-white font-medium">{{ profile.negative_count }} neg</span>
                    <span v-if="profile.high_risk_count" class="px-2 py-0.5 rounded-full bg-n-orange-9 text-white font-medium">{{ profile.high_risk_count }} risk</span>
                  </div>
                  <div class="flex gap-2 flex-shrink-0">
                    <button class="px-3 py-1 text-xs rounded-lg border transition-colors" :class="profile.active ? 'bg-n-teal-3 text-n-teal-11 border-n-teal-7' : 'bg-n-slate-3 text-n-slate-11 border-n-weak'" @click="toggleProfile(profile)">{{ profile.active ? $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_ACTIVE') : $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_PAUSED') }}</button>
                    <button class="px-3 py-1 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-n-blue-7 hover:text-n-blue-11 transition-colors" @click="checkNow(profile.id)">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_CHECK_NOW') }}</button>
                    <button class="px-3 py-1 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-n-violet-7 hover:text-n-violet-11 transition-colors" @click="startEditing(profile)">Editar</button>
                    <button class="px-3 py-1 text-xs rounded-lg border border-n-weak text-n-ruby-11 hover:border-n-ruby-7 transition-colors" @click="removeProfile(profile.id)">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_REMOVE') }}</button>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <div v-else class="flex items-center justify-center py-8 text-n-slate-11">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_NO_PROFILES') }}</div>
        </div>

        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_ALERTS_TITLE') }}</h3>
          <div class="flex items-center gap-3 mb-4 flex-wrap">
            <select v-model="alertProfileFilter" class="py-1.5 px-3 text-xs rounded-lg border bg-n-solid-3 border-n-weak text-n-slate-12">
              <option value="">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_FILTER_ALL_PROFILES') }}</option>
              <option v-for="profile in monitorProfiles" :key="profile.id" :value="profile.id">@{{ profile.username }}</option>
            </select>
            <button class="px-3 py-1 text-xs rounded-lg border transition-colors" :class="alertFilter === 'all' ? 'bg-n-blue-9 text-white border-n-blue-9' : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'" @click="alertFilter = 'all'">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_FILTER_ALL') }}</button>
            <button class="px-3 py-1 text-xs rounded-lg border transition-colors" :class="alertFilter === 'unread' ? 'bg-n-amber-9 text-white border-n-amber-9' : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'" @click="alertFilter = 'unread'">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_FILTER_UNREAD') }}</button>
            <button class="px-3 py-1 text-xs rounded-lg border transition-colors" :class="alertFilter === 'positive' ? 'bg-n-teal-9 text-white border-n-teal-9' : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'" @click="alertFilter = 'positive'">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_FILTER_POSITIVE') }}</button>
            <button class="px-3 py-1 text-xs rounded-lg border transition-colors" :class="alertFilter === 'negative' ? 'bg-n-ruby-9 text-white border-n-ruby-9' : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'" @click="alertFilter = 'negative'">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_FILTER_NEGATIVE') }}</button>
            <button class="px-3 py-1 text-xs rounded-lg border transition-colors" :class="alertFilter === 'high_risk' ? 'bg-n-orange-9 text-white border-n-orange-9' : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'" @click="alertFilter = 'high_risk'">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_FILTER_HIGH_RISK') }}</button>
          </div>
          <div v-if="filteredAlerts.length" class="space-y-3">
            <div v-for="alert in paginatedAlerts" :key="alert.id" class="flex items-start gap-4 p-3 rounded-lg border transition-colors" :class="alert.read ? 'border-n-weak' : 'border-n-blue-7 bg-n-blue-2'">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1 flex-wrap">
                  <span class="text-sm font-medium text-n-slate-12">@{{ alert.profile_username }}</span>
                  <span class="text-xs px-2 py-0.5 rounded-full bg-n-amber-3 text-n-amber-11">{{ alertTypeLabel(alert.alert_type) }}</span>
                  <span class="text-xs px-2 py-0.5 rounded-full text-white" :class="sentimentClass(alert.sentiment)">{{ sentimentLabel(alert.sentiment) }}</span>
                  <span class="text-xs px-2 py-0.5 rounded-full text-white" :class="riskClass(alert.risk_level)">{{ riskLabel(alert.risk_level) }}</span>
                  <span v-if="alert.source_platform" class="text-xs px-2 py-0.5 rounded-full text-white" :class="platformClass(alert.source_platform)">{{ platformLabel(alert.source_platform) }}</span>
                  <span v-if="alert.matched_keyword" class="text-xs px-2 py-0.5 rounded-full bg-n-violet-3 text-n-violet-11">{{ alert.matched_keyword }}</span>
                </div>
                <p v-if="alert.caption" class="text-sm text-n-slate-11 truncate">{{ alert.caption }}</p>
                <p v-if="alert.ai_summary" class="text-xs text-n-slate-11 mt-1">{{ alert.ai_summary }}</p>
                <div class="flex items-center gap-3 mt-1 text-xs text-n-slate-9">
                  <span v-if="alert.media_timestamp">Publicado: {{ formatDate(alert.media_timestamp) }}</span>
                  <span v-if="alert.source_platform" class="px-1.5 py-0.5 rounded bg-n-solid-3 text-n-slate-11">{{ alert.source_platform }}</span>
                  <span>Detectado: {{ formatDate(alert.created_at) }}</span>
                </div>
              </div>
              <div class="flex gap-2 flex-shrink-0">
                <button v-if="!alert.read" class="px-3 py-1 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-n-blue-7 hover:text-n-blue-11 transition-colors" @click="markRead(alert.id)">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_MARK_READ') }}</button>
                <a v-if="alert.permalink" :href="alert.permalink" target="_blank" rel="noopener noreferrer" class="px-3 py-1 text-xs rounded-lg border border-n-weak text-n-blue-11 hover:border-n-blue-7 transition-colors">{{ $t('INSTAGRAM_MEDIA_REPORTS.OPEN_LINK') }}</a>
              </div>
            </div>
            <div v-if="totalAlertPages > 1" class="flex items-center justify-center gap-2 pt-4">
              <button class="px-3 py-1 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-n-blue-7 hover:text-n-blue-11 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" :disabled="alertsPage <= 1" @click="alertsPage--">Anterior</button>
              <span class="text-xs text-n-slate-11">Página {{ alertsPage }} de {{ totalAlertPages }} ({{ filteredAlerts.length }} alertas)</span>
              <button class="px-3 py-1 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-n-blue-7 hover:text-n-blue-11 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" :disabled="alertsPage >= totalAlertPages" @click="alertsPage++">Próxima</button>
            </div>
          </div>
          <div v-else class="flex items-center justify-center py-8 text-n-slate-11">{{ $t('INSTAGRAM_MEDIA_REPORTS.MONITOR_NO_ALERTS') }}</div>
        </div>
      </template>
    </template>
  </div>
</template>
